import {
  MONTHS_BILLED_PER_ANNUAL_SELF_SERVE,
  type PlanDefinition,
} from "@/features/billing/plans"
import { formatStorageBytes } from "@/lib/billing/usage-math"

/** Canonical “two months free” annual model (10 months billed at list monthly). */
export function selfServeAnnualPrepayExplanation(): string {
  const billed = MONTHS_BILLED_PER_ANNUAL_SELF_SERVE
  const freeMonths = 12 - billed
  return `Annual prepay bills ${billed} months at the monthly rate (${freeMonths} months free).`
}

function retentionSentence(days: number): string {
  if (days >= 1800) return "Long-term guest access to your hosted story"
  if (days >= 300) return "Extended guest access window"
  if (days >= 30) return "About a year of guest access to memories you host"
  return "Shorter guest access window on the free tier"
}

function storageAddOnPublicLine(def: PlanDefinition): string {
  const { maxSelfServeChunks, chunkSizeBytes } = def.storageAddOn
  if (def.id === "free") {
    return "More storage when you upgrade to a paid plan"
  }
  if (maxSelfServeChunks <= 0) {
    return "Storage add-on blocks: not offered on this tier"
  }
  return `Optional storage: up to ${maxSelfServeChunks} × ${formatStorageBytes(chunkSizeBytes)} blocks`
}

/**
 * Public plan bullets for pricing / Plan & billing — storage-led, no “unlimited”
 * claims for Enterprise (contact-only positioning).
 */
export function planTierFeatureBullets(def: PlanDefinition): readonly string[] {
  if (def.id === "enterprise") {
    return [
      "Custom limits, storage, and retention—defined in your agreement",
      "Security, procurement, and rollout support with our team",
      "Contact sales for pricing and scope",
    ] as const
  }

  const e = def.entitlements
  const out: string[] = []
  if (e.maxStorageBytes >= 0) {
    out.push(`${formatStorageBytes(e.maxStorageBytes)} included storage`)
  }
  out.push(retentionSentence(e.retentionPeriodDays))
  if (def.id === "free") {
    out.push("Host events with photos; video uploads not included")
  } else {
    out.push("Photo & video albums, co-host tools, and richer guest story")
  }
  if (def.featureFlags.smsOutbound && e.maxSmsPerMonth > 0) {
    out.push(`SMS to guests (up to ${e.maxSmsPerMonth} / month)`)
  }
  out.push(storageAddOnPublicLine(def))
  return out
}
